// ─────────────────────────────────────────────────────────────────────────────
// ProductsTabComponent
// ─────────────────────────────────────────────────────────────────────────────
// Displays the Knowledge Base products tab content.
// Responsibilities:
//   - Render Level 2 sub-tabs (External References | Alias | Ignore List | Preferred Products)
//   - Show contextual info banner per sub-tab
//   - Host sub-tab content (ListExternalReferencesComponent, ListAliasesComponent, ListIgnoreCodesComponent, ListPreferredProductsComponent)
//   - Persist active sub-tab via URL query params
// ─────────────────────────────────────────────────────────────────────────────

import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';

import { SvgIconComponent } from 'src/app/ui/svg-icon/svg-icon.component';
import { ListAliasesComponent } from '../list-aliases/list-aliases.component';
import { ListExternalReferencesComponent } from '../list-external-references/list-external-references.component';
import { ListIgnoreCodesComponent } from '../list-ignore-codes/list-ignore-codes.component';
import { ListPreferredProductsComponent } from '../list-preferred-products/list-preferred-products.component';

@Component({
  selector: 'app-products-tab',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    SvgIconComponent,
    ListAliasesComponent,
    ListExternalReferencesComponent,
    ListIgnoreCodesComponent,
    ListPreferredProductsComponent,
  ],
  templateUrl: './products-tab.component.html',
  styleUrls: ['./products-tab.component.scss'],
})
export class ProductsTabComponent implements OnInit, OnDestroy {
  // ── Tab state ──────────────────────────────────────────────────────────

  activeL2Tab: 'external-references' | 'aliases' | 'ignore-list' | 'preferred-products' = 'external-references';

  // ── Cleanup ────────────────────────────────────────────────────────────

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const subtab = params['subtab'];
      if (subtab === 'aliases') {
        this.activeL2Tab = 'aliases';
      } else if (subtab === 'ignore-list') {
        this.activeL2Tab = 'ignore-list';
      } else if (subtab === 'preferred-products') {
        this.activeL2Tab = 'preferred-products';
      } else {
        this.activeL2Tab = 'external-references';
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Tab navigation ─────────────────────────────────────────────────────

  onL2TabChange(subtab: 'external-references' | 'aliases' | 'ignore-list' | 'preferred-products'): void {
    this.activeL2Tab = subtab;
    this.router.navigate([], { queryParams: { subtab }, queryParamsHandling: 'merge' });
  }
}
